import { EnLocationPage } from "@/components/pages/LocationPageEn";
// EN location detail: /en/locations/$slug
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { getLocation, locations } from "@/data/locations";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { LocationDeepLinks } from "@/components/LocationDeepLinks";
import { EntityPanel } from "@/components/EntityPanel";

const SITE = "https://nyclanguageinstitute.com";
const ORG_NAME = "NYC Legal & Notary Service Co., Ltd.";
const ORG_PHONE = "+66-83-249-4999";

export const Route = createFileRoute("/en/locations/$slug")({
  loader: ({ params }) => {
    const loc = getLocation(params.slug);
    if (!loc) throw notFound();
    return { loc };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData)
      return { meta: [{ title: ctaTitle("Location — NYC Legal", "/en/locations/$slug", "en") }] };
    const { loc } = loaderData;
    const enUrl = `${SITE}/en/locations/${params.slug}`;
    const title = `Legal, Notary & Translation in ${loc.nameEn} | NYC Language Institute Thailand`;
    const desc = `Certified translation, Notary Public, NAATI, MFA legalisation and visa support for clients in ${loc.nameEn} (${loc.region}). Call +66 83 249 4999. Ask us before you file — it's free.`;

    // Google-compliance: only the real offices may claim a PostalAddress.
    // Coverage areas are emitted as a Service with areaServed.
    const isOffice = loc.type === "headquarters" || loc.type === "branch";
    const mapsUrl =
      loc.mapUrl ??
      (loc.lat && loc.lng
        ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.nameEn)}`);

    const jsonLd = isOffice
      ? {
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: `${ORG_NAME} — ${loc.nameEn}`,
          url: enUrl,
          inLanguage: "en",
          telephone: ORG_PHONE,
          priceRange: "฿฿",
          hasMap: mapsUrl,
          areaServed: { "@type": "AdministrativeArea", name: loc.nameEn },
          address: {
            "@type": "PostalAddress",
            streetAddress: loc.addressEn ?? loc.address,
            addressLocality: loc.region === "กรุงเทพมหานคร" ? "Bangkok" : loc.nameEn,
            postalCode: loc.postalCode,
            addressCountry: "TH",
          },
          ...(loc.lat && loc.lng
            ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
            : {}),
          openingHours: "Mo-Sa 09:00-18:00",
        }
      : {
          "@context": "https://schema.org",
          "@type": "Service",
          name: `Certified Translation, Notary Public & Legalisation — ${loc.nameEn}`,
          url: enUrl,
          inLanguage: "en",
          serviceType: "Certified translation, Notary Public, Apostille & MFA legalisation",
          providerMobility: "dynamic",
          areaServed: {
            "@type": "AdministrativeArea",
            name: loc.nameEn,
            ...(loc.lat && loc.lng
              ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
              : {}),
          },
          provider: {
            "@type": "LegalService",
            name: ORG_NAME,
            url: SITE,
            telephone: ORG_PHONE,
          },
        };

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "EN Home", item: `${SITE}/en` },
        { "@type": "ListItem", position: 2, name: "Locations", item: `${SITE}/en/locations` },
        { "@type": "ListItem", position: 3, name: loc.nameEn, item: enUrl },
      ],
    };

    return {
      meta: [
        { title: ctaTitle(title, "/en/locations/$slug", "en") },
        { name: "description", content: ctaDesc(desc.slice(0, 158), "/en/locations/$slug", "en") },
        { property: "og:title", content: ctaTitle(title, "/en/locations/$slug", "en") },
        {
          property: "og:description",
          content: ctaDesc(desc.slice(0, 158), "/en/locations/$slug", "en"),
        },
        { property: "og:url", content: enUrl },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: enUrl },
        ...hreflangLinks(`/locations/${params.slug}`, `/en/locations/${params.slug}`),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
      ],
    };
  },
  component: EnLocationPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Location not found</h1>
      <Link to="/en/locations" className="mt-4 inline-block text-amber-300 hover:underline">
        ← Back to coverage
      </Link>
    </main>
  ),
});
